/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
const sites = require('./sites.js')

// cache for 15 minutes and serve the stale version for another 4 hours while revalidating
const DEFAULT_CACHE = `public, max-age=${60 * 15}, stale-while-revalidate=${60 * 60 * 4}`
// cache for 4 hours and serve the stale version for another 1 day while revalidating
const CACHE_4h = `public, max-age=${60 * 60 * 4}, stale-while-revalidate=${60 * 60 * 24 * 1}`

/**
 * Default configuration for the app.
 *
 * IMPORTANT: Process environment variables are accessible only on SSR and not on the client side.
 * To use specific parameters on the client side, you must use the appropriate config file:
 * - `config/<environment>.js` for environment-specific configurations
 *
 * Process environment variables are mostly useful for local development server.
 * Use `.env.<environment>` files to set environment variables for local development.
 */
module.exports = {
    app: {
        url: {
            site: 'path',
            locale: 'path',
            showDefaults: true
        },
        login: {
            passwordless: {
                enabled: false,
                callbackURI:
                    global.process?.env?.PASSWORDLESS_LOGIN_CALLBACK_URI ||
                    '/passwordless-login-callback',
                landingPath: '/passwordless-login-landing'
            },
            social: {
                enabled: false,
                idps: ['google', 'apple'],
                redirectURI: global.process?.env?.SOCIAL_LOGIN_REDIRECT_URI || '/social-callback'
            },
            resetPassword: {
                callbackURI:
                    global.process?.env?.RESET_PASSWORD_CALLBACK_URI || '/reset-password-callback',
                landingPath: '/reset-password-landing'
            }
        },
        defaultSite: 'stokke',
        siteAliases: {},
        sites,
        seo: {
            noIndex: true
        },
        commerceAPI: {
            proxyPath: `/mobify/proxy/api`,
            parameters: {
                clientId:
                    global.process?.env?.PWA_KIT_SLAS_CLIENT_ID ||
                    '24a927f6-fb87-42c2-9148-4a450b9fb289',
                organizationId: global.process?.env?.PWA_KIT_ORG_ID || 'f_ecom_aaqf_001',
                shortCode: '9z83zsl4',
                siteId: 'AUT'
            }
        },
        einsteinAPI: {
            host: 'https://api.cquotient.com',
            einsteinId: '79b89e8d-799f-479a-93b1-4dc3741985cc',
            // This differs from the siteId in commerceAPIConfig for testing purposes
            siteId: 'aaqf-AUT',
            isProduction: false
        },
        bazaarvoiceAPI: {
            proxies: {
                prd: {
                    host: 'https://api.bazaarvoice.com',
                    proxyPath: '/proxy/bv/prd'
                },
                stg: {
                    host: 'https://stg.api.bazaarvoice.com',
                    proxyPath: '/proxy/bv/stg'
                }
            },
            apiVersion: '5.4',
            cacheControlHeader: [
                'proxy-revalidate',
                `s-maxage=300`, // 's-maxage=3300',(prd)
                `max-age=90`, // 'max-age=900',
                `stale-while-revalidate=240` // 'stale-while-revalidate=2400'
            ].join(', ')
        },
        japanPostAPI: {
            proxyPath: `/mobify/proxy/japanpost`
        },
        googleMapsAPI: {
            // ref: https://developers.google.com/maps/documentation/javascript/versions?hl=en#quarterly-updates
            version: 'weekly',
            // ref: https://developers.google.com/maps/documentation/javascript/libraries
            libraries: [
                // ref: https://developers.google.com/maps/documentation/javascript/places
                'places'
            ]
            // region and language parameters are automatically provided based on the locale
            // additional properties used for googlemaps loader
            // ref: https://googlemaps.github.io/js-api-loader/interfaces/LoaderOptions.html
        },
        googleTranslateAPI: {
            host: 'https://translation.googleapis.com/language/translate/v2'
        },
        gtmAPI: {
            gtmId: 'GTM-N6HMPT8'
        },
        cookiebotAPI: {
            cookiebotId: 'e2f873d9-2ee6-420d-b222-9cf53fc30ec0'
        },
        curalateAPI: {
            proxyPath: `/mobify/caching/curalate`
        },
        continents: {
            Africa: ['en-EG', 'en-ZA'],
            Asia: [
                'en-QA',
                'en-AE',
                'zh-CN',
                'zh-HK',
                'zh-TW',
                'en-AM',
                'en-AZ',
                'en-GE',
                'en-HK',
                'en-IN',
                'en-IR',
                'en-IL',
                'en-KZ',
                'en-MY',
                'en-QA',
                'en-SG',
                'en-TR',
                'ja-JP',
                'ko-KR',
                'tr-TR'
            ],
            Europe: [
                'da-DK',
                'nl-BE',
                'nl-NL',
                'en-AL',
                'en-BY',
                'en-BA',
                'en-BG',
                'en-HR',
                'en-CZ',
                'en-CY',
                'en-EE',
                'en-FI',
                'en-GR',
                'en-HU',
                'en-IS',
                'en-IE',
                'en-LV',
                'en-LT',
                'en-PT',
                'en-RO',
                'en-RS',
                'en-SK',
                'en-SI',
                'en-UA',
                'en-GB',
                'fr-BE',
                'fr-FR',
                'fr-LU',
                'fr-CH',
                'de-AT',
                'de-DE',
                'de-LU',
                'de-CH',
                'it-IT',
                'it-CH',
                'no-NO',
                'no-UA',
                'pl-PL',
                'es-ES',
                'sv-SE'
            ],
            North_America: ['en-CA', 'en-US', 'fr-CA', 'es-CR', 'es-MX', 'es-PA', 'es-PR', 'es-US'],
            Oceania: ['en-AU', 'en-NZ'],
            South_America: ['pt-BR', 'es-AR', 'es-CL', 'es-CO', 'es-PE']
        },
        staticAPI: {
            proxyPath: '/mobify/proxy/static'
        },
        storefrontAPI: {
            host: global.process?.env?.SF_STOREFRONT_HOST,
            proxyPath: `/mobify/proxy/sapi`,
            parameters: {
                storefrontPassword:
                    global.process?.env?.STOREFRONT_PASSWORD != null
                        ? global.process?.env?.STOREFRONT_PASSWORD
                        : 'stokke1932',
                siteId: 'AUT'
            }
        },
        // cache configuration per page type
        cache: {
            cart: DEFAULT_CACHE,
            customerService: CACHE_4h,
            home: DEFAULT_CACHE,
            pdp: DEFAULT_CACHE,
            pep: DEFAULT_CACHE,
            plp: DEFAULT_CACHE,
            content: DEFAULT_CACHE,
            login: DEFAULT_CACHE,
            registration: DEFAULT_CACHE,
            resetPassword: DEFAULT_CACHE,
            momentvm: DEFAULT_CACHE
        },
        // query cache configuration
        queryCache: {
            // price and availability data
            availabilityPrice: {
                staleTime: 15 * 60 * 1000 // 15 min
            },
            // infrequently changing data
            static: {
                // reuse ssr cached data for 4 hours and 15 minutes
                // matches the proxy cache + stale-while-revalidate
                // so loading the page will not trigger immediately a new request
                // note: this cache is limited to the browser window and
                // any page reload will request fresh data
                staleTime: (4 * 60 + 15) * 60 * 1000
            }
        },
        // boolean that handles the visibility of stack trace in error page on development
        displayErrorStackTrace: true,
        // flag to indicate if exploration test is enabled if not home redirection
        explorationTestEnabled: true
    },
    externals: [],
    pageNotFoundURL: '/page-not-found',
    // max output size in bytes above which we switch to a client side rendering
    ssrMaxResponseSize: Number.parseInt(global.process?.env?.MAX_RESPONSE_SIZE, 10),
    ssrEnabled: true,
    ssrOnly: ['ssr.js', 'ssr.js.map', 'node_modules/**/*.*'],
    ssrShared: [
        'static/ico/favicon.ico',
        'static/apple-developer-merchantid-domain-association.txt',
        'static/robots.txt',
        '**/*.js',
        '**/*.js.map',
        '**/*.json'
    ],
    ssrParameters: {
        // keep in sync with engines.node in package.json - scripts/node-version.test.js guards it
        ssrFunctionNodeVersion: '24.x',
        proxyConfigs: [
            {
                host: '9z83zsl4.api.commercecloud.salesforce.com',
                path: 'api'
            },
            {
                host:
                    global.process?.env?.SF_PROXY_TARGET ||
                    'aaqf-001.dx.commercecloud.salesforce.com',
                path: 'sapi'
            },
            {
                host:
                    global.process?.env?.SF_PROXY_TARGET ||
                    'aaqf-001.dx.commercecloud.salesforce.com',
                path: 'ocapi'
            },
            {
                host:
                    global.process?.env?.SF_PROXY_TARGET ||
                    'aaqf-001.dx.commercecloud.salesforce.com',
                path: 'static'
            },
            {
                host: 'edge.curalate.com',
                path: 'curalate'
            },
            {
                host: 'stub-qz73x.da.pf.japanpost.jp',
                path: 'japanpost'
            }
        ]
    }
}
